import * as Sentry from '@sentry/browser';

/**
 * Sentry 에러 로그 제외 대상 목록
 */
export const SENTRY_IGNORE_ERROR_LIST = [
  /**
   * 우리 서비스에서 발생하고 있는 ResizeObserver 에러의 경우 에러 처리 난이도에 비해 서비스 영향은 크지 않아 무시
   *   - ref. https://sentry.io/answers/react-resizeobserver-loop-completed-with-undelivered-notifications/
   */
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
  /** 외부 스크립트에서 발생하였으나 CORS 정책으로 인해 정보가 제공되지 않는 에러 */
  'Script error.',
];

const DEBOUNCE_TIME = 60_000;

/** 로그 메시지 캐시, 디바운싱 용도 */
const messageCache = new Map();

/** 센트리 로그 허용 경로 */
const ALLOWED_PATHS = ['/live', '/station'];

/**
 * 센트리 클라이언트 객체, 연결 정보 및 센트리 동작 설정
 */
const SoopSentryClient = new Sentry.BrowserClient({
  dsn: process.env.SENTRY_DSN,
  environment: process.env.BUILD_ENV,
  tracesSampleRate: 1.0,
  release: process.env.SENTRY_RELEASE_VERSION,
  sampleRate: 1.0,
  debug: true,
  ignoreErrors: SENTRY_IGNORE_ERROR_LIST,
  /**
   * Note.
   * Sentry 서비스의 부하를 줄이기 위해 동일 메시지에 대한 로그는 정해진 시간 동안 1번만 전송 하도록 구현
   */
  beforeSend(event) {
    // 로컬 실행 시 로그 전송 제외
    if (process.env.NODE_ENV === 'development') {
      return null;
    }

    const message = event.message || event.exception?.values?.[0]?.value;

    /** 로그 요청 발생 시간 */
    const now = Date.now();

    // 로그 판별 전 만료된 메시지에 해당하는 캐시 삭제
    for (const [message, timestamp] of messageCache.entries()) {
      if (now - timestamp >= DEBOUNCE_TIME) {
        messageCache.delete(message);
      }
    }

    const currentPath = window.location.pathname;

    // 에러 레벨이 fatal 이 아니면 로그를 보내지 않음
    if (event?.level !== 'fatal') {
      return null;
    }

    if (message) {
      const lastTimestamp = messageCache.get(message);

      // 디바운싱, 아직 캐시에 에러가 남아있다면 만료되지 않았음을 의미
      if (lastTimestamp) {
        return null;
      }

      // 현재 에러를 캐시에 등록
      messageCache.set(message, now);
    }

    if (event && event.extra) {
      event.extra.currentPath = currentPath;
    } else if (event) {
      event.extra = {currentPath: currentPath};
    }
    return event;
  },
});

export const SoopSentryHub = new Sentry.Hub(SoopSentryClient);
